// Helper to treat backend timestamps as UTC if no timezone info is present
export function normalizeToUtc(dateInput) {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;
  if (typeof dateInput !== "string") return dateInput;

  let str = dateInput.trim();
  if (!str) return null;

  // Replace space between date and time with T if present (e.g., "2026-07-22 05:06:00")
  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}/.test(str)) {
    str = str.replace(" ", "T");
  }

  // If it already has Z or offset, trust it
  if (str.endsWith("Z") || str.includes("+")) {
    return str;
  }
  // Otherwise, append Z to force UTC interpretation
  return `${str}Z`;
}

// --- Core Conversion Helpers ---

/**
 * Converts a UTC date string (or Date object) to a Local Date object.
 * accounts for missing 'Z' by using normalizeToUtc.
 * @param {string|Date} dateStr
 * @returns {Date|null} Local Date object
 */
export function getLocalDateTime(dateStr) {
  if (!dateStr) return null;
  const normalized = normalizeToUtc(dateStr);
  const dateObj = new Date(normalized);
  if (isNaN(dateObj.getTime())) return null;
  return dateObj;
}

/**
 * Converts a Local Date object to a UTC ISO String.
 * @param {Date} localDate
 * @returns {string|null} ISO String (UTC)
 */
export function toUTCISOString(localDate) {
  if (!localDate) return null;
  const date = new Date(localDate);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
}

/**
 * Combines a local date input (Date object or string) and a time string (HH:mm)
 * and returns a UTC ISO string.
 * Used for sending user-selected scheduling times to backend.
 * @param {Date|string} dateInput
 * @param {string} timeInput
 * @returns {string|null} UTC ISO String
 */
export function convertToUTC(dateInput, timeInput) {
  if (!dateInput) return null;

  try {
    let dateObj;
    // Fix: Treat YYYY-MM-DD strings as local date components to avoid UTC midnight shift
    if (
      typeof dateInput === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateInput)
    ) {
      const [y, m, d] = dateInput.split("-").map(Number);
      dateObj = new Date(y, m - 1, d);
    } else {
      dateObj = new Date(dateInput);
    }

    if (isNaN(dateObj.getTime())) return null;

    // Create a new date object from the input date in local time
    const localDate = new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
    );

    if (timeInput) {
      const [hours, minutes] = timeInput.split(":").map(Number);
      if (!isNaN(hours) && !isNaN(minutes)) {
        localDate.setHours(hours, minutes, 0, 0);
      }
    }

    return localDate.toISOString();
  } catch (error) {
    console.error("Error converting to UTC:", error);
    return null;
  }
}

// --- Formatters ---

/**
 * Formats a date for display in the UI (Local Time).
 * Handles 'Today', 'Yesterday' logic.
 */
export function formatDisplayDate(dateString) {
  const date = getLocalDateTime(dateString);
  if (!date) return "N/A";

  try {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const fullDateTime = `${day}/${month}/${year} ${time}`;

    if (date.toDateString() === today.toDateString()) {
      return `${fullDateTime} (Today)`;
    }

    if (date.toDateString() === yesterday.toDateString()) {
      return `${fullDateTime} (Yesterday)`;
    }

    return fullDateTime;
  } catch (error) {
    return "N/A";
  }
}

export function newFormatDisplayDate(dateString) {
  const date = getLocalDateTime(dateString);
  if (!date) return "N/A";

  return date.toLocaleDateString("en-GB");
}

export function formatRegistrationDate(dateString) {
  const date = getLocalDateTime(dateString);
  if (!date) return "N/A";

  const now = new Date(); // Browser local time
  // Calculation should ideally be based on timestamps to avoid timezone mess, Date objects are good
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return "Today";
  if (diffDays === 2) return "Yesterday";
  if (diffDays <= 7) return `${diffDays - 1} days ago`;

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}, ` + date.toLocaleTimeString('en-US', {
    hour12: true,
    hour: 'numeric',
    minute: '2-digit'
  });
}

export function formatTime(dateString) {
  if (!dateString) return "";

  if (typeof dateString === "string") {
    const trimmed = dateString.trim();
    const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 === 0 ? 12 : hours % 12;
      const strHours = String(hours).padStart(2, "0");
      return `${strHours}:${minutes} ${ampm}`;
    }
  }

  const date = getLocalDateTime(dateString);
  if (!date) return "";

  return date.toLocaleTimeString("en-US", {
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(dateString) {
  const date = getLocalDateTime(dateString);
  if (!date) return "";

  const now = new Date();

  if (date.toDateString() === now.toDateString()) {
    return "Today";
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// Typical Notification display
export const formatNotificationTime = (dateString) => {
  const date = getLocalDateTime(dateString);
  if (!date) return "";

  const now = new Date();

  // Use timestamps for difference calculation to avoid timezone issues
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Today (within last 24 hours AND on the same calendar day)
  if (date.toDateString() === now.toDateString()) {
    return `Today, ${time}`;
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${time}`;
  }

  // Older → full date + time
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}, ${time}`;
};
/**
 * Returns a YYYY-MM-DD string representing the local date.
 * Useful for calendar grids and APIs expecting local date strings.
 * @param {Date} date
 * @returns {string} YYYY-MM-DD string
 */
export function getLocalISODate(date) {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Converts a UTC date string and time string to a local HH:mm string.
 * @param {string} dateStr - YYYY-MM-DD
 * @param {string} timeStr - HH:mm:ss (UTC)
 * @returns {string} HH:mm in local time
 */
export function getLocalTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  const normalized = normalizeToUtc(`${dateStr}T${timeStr}`);
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return "";

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  return `${hours}:${minutes} ${ampm}`;
}

export function normalizeToUtc1(dateInput) {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput.toISOString();

  if (typeof dateInput !== "string") return dateInput;

  // Already has timezone info
  if (dateInput.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(dateInput)) {
    return dateInput;
  }

  // Backend sends UTC WITHOUT Z → append Z
  return `${dateInput}Z`;
}

/**
 * Carefully parses a date string, supporting DD-MM-YYYY and YYYY-MM-DD.
 * @param {string} dateStr
 * @returns {Date|null}
 */
export function parseDate(dateStr) {
  if (!dateStr) return null;

  // Handle DD-MM-YYYY
  const ddmm = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddmm) {
    const [_, d, m, y] = ddmm;
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }

  // Handle YYYY-MM-DD
  const yymm = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yymm) {
    const [_, y, m, d] = yymm;
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }

  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Parses a YYYY-MM-DD date string and a HH:mm:ss time string literally into a local Date object,
 * avoiding any UTC/local timezone shifts.
 * @param {string} dateStr - YYYY-MM-DD
 * @param {string} timeStr - HH:mm:ss
 * @returns {Date|null} Local Date object
 */
export function parseLiteralDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const dateParts = dateStr.split("-").map(Number);
  if (dateParts.length !== 3 || isNaN(dateParts[0])) return null;

  const timeParts = (timeStr || "00:00:00").split(":").map(Number);
  return new Date(
    dateParts[0],
    dateParts[1] - 1,
    dateParts[2],
    timeParts[0] || 0,
    timeParts[1] || 0,
    timeParts[2] || 0
  );
}
